import type { MeasurementGuide, MeasurementMode } from '@planner/config';
import { useEffect, useRef, useState, type MouseEvent } from 'react';
import { measurementPresentation } from '../../appdata.js';

export function GuideDownload({ mode, guide }: { mode: MeasurementMode; guide: MeasurementGuide }) {
  const { guideLabel, guideUrl } = measurementPresentation[mode];
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const request = useRef<AbortController | null>(null);

  useEffect(() => () => request.current?.abort(), []);

  const download = async (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    if (request.current) {
      return;
    }

    const controller = new AbortController();
    request.current = controller;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(guideUrl, { signal: controller.signal });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const contentType = response.headers.get('content-type')?.split(';')[0]?.trim().toLowerCase();
      if (contentType !== 'application/pdf') {
        throw new Error('the response is not a PDF');
      }
      const data = await response.arrayBuffer();
      const bytes = new Uint8Array(data);
      const decoder = new TextDecoder();
      if (
        decoder.decode(bytes.subarray(0, 5)) !== '%PDF-' ||
        !decoder.decode(bytes.subarray(Math.max(0, bytes.length - 1024))).includes('%%EOF')
      ) {
        throw new Error('the PDF is incomplete or invalid');
      }
      if (controller.signal.aborted) {
        return;
      }
      const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = guide.filename;
      document.body.append(link);
      try {
        link.click();
      } finally {
        link.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      }
    } catch (reason) {
      if (!controller.signal.aborted) {
        const detail = reason instanceof Error ? reason.message : String(reason);
        setError(`The guide could not be downloaded (${detail}). Please try again.`);
      }
    } finally {
      if (!controller.signal.aborted) {
        request.current = null;
        setLoading(false);
      }
    }
  };

  return (
    <div className="text-sm">
      <a
        data-name={`measure-guide-${mode}`}
        href={guideUrl}
        download={guide.filename}
        aria-disabled={loading || undefined}
        onClick={(event) => void download(event)}
        className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 text-xs font-medium text-gray-700 focus-visible:outline-2 focus-visible:outline-brand aria-disabled:cursor-wait aria-disabled:opacity-60"
      >
        <svg
          aria-hidden="true"
          className="size-4"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          viewBox="0 0 24 24"
        >
          <path d="M12 10v6m0 0-3-3m3 3 3-3m2 8H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5.586a1 1 0 0 1 .707.293l5.414 5.414a1 1 0 0 1 .293.707V19a2 2 0 0 1-2 2Z" />
        </svg>
        {loading ? 'Preparing guide...' : guideLabel}
      </a>
      {error && (
        <p role="alert" className="mt-1 text-red-700">
          {error}
        </p>
      )}
    </div>
  );
}
